import { useEffect, useState } from "react";
import {
  assessmentPhotoViewLabel,
  type AssessmentPhotoView,
  type PatientPhotoMeta,
} from "@care-by-ella/contracts";
import {
  fetchPatientPhotoBlob,
  fetchPatientPhotos,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-381 — the "Bilder" section on the patient page: the body photos the
// patient attached to milestone check-ins (context 'milestone:<n>') or
// uploaded freeform. The list is metadata-only; clicking a row fetches the
// bytes through the authenticated employee endpoint (session cookie — a bare
// <img src> can't carry it) and renders them via an object URL.

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function contextLabel(context: string): string {
  const m = /^milestone:(\d+)$/.exec(context);
  if (m) return `Avstämning månad ${m[1]}`;
  if (context === "freeform") return "Fritt uppladdad";
  // TES-544 — the "Bild" choice under "Din hälsa över tid" in the app.
  if (context === "dina_varden") return "Från Din hälsa över tid";
  // TES-1648 — the written track's three photos (reviewed under
  // "Patientbilder" on the overview).
  const view = /^assessment:(front|side|back)$/.exec(context);
  if (view) {
    return `Patientbild ${assessmentPhotoViewLabel(
      view[1] as AssessmentPhotoView,
    ).toLowerCase()}`;
  }
  return context;
}

function PatientPhotosPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const [photos, setPhotos] = useState<PatientPhotoMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPatientPhotos(patientId)
      .then((r) => {
        if (!cancelled) setPhotos(r.photos);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorized();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, onUnauthorized]);

  // Revoke the object URL when the viewer closes / switches / unmounts.
  useEffect(() => {
    return () => {
      if (openUrl) URL.revokeObjectURL(openUrl);
    };
  }, [openUrl]);

  async function view(photo: PatientPhotoMeta) {
    if (openId === photo.id) {
      setOpenId(null);
      setOpenUrl(null);
      return;
    }
    setLoadingId(photo.id);
    setError(null);
    try {
      const blob = await fetchPatientPhotoBlob(patientId, photo.id);
      setOpenId(photo.id);
      setOpenUrl(URL.createObjectURL(blob));
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingId(null);
    }
  }

  // TES-520 — an empty section is a line, not a card. The card chrome is
  // for content; a box around "Inga bilder än" is the thing that made the
  // overview look like a stack of empty drawers.
  if (photos !== null && photos.length === 0 && !error) {
    return (
      <p className="text-sm text-textSecondary">
        Inga bilder än — patienten laddar upp via månadsavstämningen.
      </p>
    );
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-sm">Bilder</h2>
      {error ? <p className="text-danger text-sm mb-sm">{error}</p> : null}
      {photos === null ? (
        <p className="text-textSecondary text-sm">Hämtar…</p>
      ) : photos.length === 0 ? (
        <p className="text-textSecondary text-sm">Inga bilder än.</p>
      ) : (
        <ul className="flex flex-col gap-xs text-sm">
          {photos.map((p) => (
            <li
              key={p.id}
              className="border-b border-surfaceMuted last:border-b-0 py-xs"
            >
              <button
                type="button"
                onClick={() => void view(p)}
                className="flex w-full items-center justify-between gap-md text-left hover:text-brand"
              >
                <span>
                  {contextLabel(p.context)}
                  <span className="text-textSecondary text-xs ml-sm">
                    {p.mimeType} • {Math.round(p.byteSize / 1024)} kB
                  </span>
                </span>
                <span className="text-textSecondary text-xs">
                  {loadingId === p.id ? "Hämtar…" : fmt(p.createdAt)}
                </span>
              </button>
              {openId === p.id && openUrl ? (
                <img
                  src={openUrl}
                  alt={contextLabel(p.context)}
                  className="mt-sm max-h-[480px] rounded-sm border border-surfaceMuted"
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// TES-1040 — hidden in the admin's read-only support view: body photos are not opened to an admin (the API answers 403), so the
// section is left out rather than shown failing.
export const PatientPhotos = hiddenWhenReadOnly(PatientPhotosPanel);
